import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {Deployment, DeploymentPageInfo} from '../types'
import {DeploymentRecordListItem} from './DeploymentRecordListItem'
import styles from './DeploymentRecordList.module.css'
import {Blankslate} from '@primer/react/experimental'
import {Heading, Link, Pagination} from '@primer/react'
import {useSearchParams} from '@github-ui/use-navigate'
import {useCallback} from 'react'
import {number as formatNumber} from '@github-ui/formatters'

interface DeploymentRecordListProps {
  deployments: Deployment[]
  deploymentPageInfo?: DeploymentPageInfo
}

export function DeploymentRecordList({deployments, deploymentPageInfo}: DeploymentRecordListProps) {
  const [searchParams] = useSearchParams()

  const totalCount = deploymentPageInfo?.totalCount ?? deployments.length
  const listTitle = `${formatNumber(totalCount)} ${totalCount === 1 ? 'record' : 'records'}`

  const shouldShowPagination = deploymentPageInfo && deploymentPageInfo.totalPages > 1

  const hrefBuilder = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams)
      params.set('deployment_page', page.toString())
      return `?${params.toString()}`
    },
    [searchParams],
  )

  const emptyList = (
    <Blankslate border>
      <Blankslate.Heading className="f4">None detected</Blankslate.Heading>
      <Blankslate.Description>
        Link this artifact with your deployments using the{' '}
        <Link inline href="https://docs.github.com/en/rest/orgs/artifact-metadata?apiVersion=2022-11-28">
          artifact metadata API
        </Link>
      </Blankslate.Description>
    </Blankslate>
  )

  return (
    <div>
      <Heading as="h3" className={styles.Header}>
        Deployments
      </Heading>

      {totalCount === 0 ? (
        emptyList
      ) : (
        <>
          <ListView
            variant="default"
            title=""
            className="border rounded"
            metadata={<ListViewMetadata title={listTitle} />}
          >
            {deployments.map(deployment => (
              <DeploymentRecordListItem key={deployment.deployment_name} deployment={deployment} />
            ))}
          </ListView>
          {shouldShowPagination && (
            <div className="d-flex flex-justify-center tmp-mt-4">
              <Pagination
                pageCount={deploymentPageInfo.totalPages}
                currentPage={deploymentPageInfo.page}
                hrefBuilder={hrefBuilder}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
